import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    MenuItem,
    Select
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setToast } from '../../../util/toast';
import Button from '../../extra/Button';
import Input from '../../extra/Input';
import { getEligibleRoles } from '../../store/role/role.action';
import { createStaff, editStaff, getStaff } from '../../store/staff/staff.action';

const StaffDialogue = ({ page, size }) => {
    const dispatch = useDispatch();
    const { dialogueData, dialogue: open } = useSelector((state) => state.dialogue);

    const { assignableRoles } = useSelector((state) => state.roles);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        roleId: '',
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getEligibleRoles());
    }, [dispatch]);

    useEffect(() => {
        if (dialogueData) {
            setFormData({
                name: dialogueData.name || '',
                email: dialogueData.email || '',
                password: '',
                roleId: dialogueData.role?._id || '',
            });
        } else {
            setFormData({
                name: '',
                email: '',
                password: '',
                roleId: '',
            });
        }
    }, [dialogueData]);

    const handleClose = () => {
        dispatch({ type: "CLOSE_DIALOGUE" });
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: '' });
        }
    };

    const validate = () => {
        let tempErrors = {};
        if (!formData.name) tempErrors.name = "Name is required";
        if (!formData.email) tempErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) tempErrors.email = "Email is invalid";

        if (!dialogueData || (dialogueData && formData.password)) {
            if (!formData.password) {
                tempErrors.password = "Password is required";
            } else {
                const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
                if (!passwordRegex.test(formData.password)) {
                    tempErrors.password = "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.";
                }
            }
        }

        if (!formData.roleId) tempErrors.roleId = "Role is required";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = (e) => {

        e.preventDefault();
        if (!validate()) return;

        if (dialogueData) {
            // Update
            const payload = { subAdminId: dialogueData._id };
            let hasChanges = false;

            if (formData.name !== dialogueData.name) {
                payload.name = formData.name;
                hasChanges = true;
            }
            if (formData.email !== dialogueData.email) {
                payload.email = formData.email;
                hasChanges = true;
            }
            if (formData.roleId !== dialogueData.role?._id) {
                payload.roleId = formData.roleId;
                hasChanges = true;
            }
            if (formData.password) {
                payload.password = formData.password;
                hasChanges = true;
            }

            if (!hasChanges) {
                setToast("info", "No changes made to update.");
                handleClose();
                return;
            }

            dispatch(editStaff(payload)).then((res) => {
                if (res.status === true) {
                    dispatch(getStaff(page, size));
                    handleClose();
                }
            });
        } else {
            // Create
            dispatch(createStaff(formData)).then((res) => {
                if (res.status === true) {
                    dispatch(getStaff(page, size));
                    handleClose();
                }
            });
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <div className="headerTitle fw-bold text-white">
                        {dialogueData ? 'Edit Staff' : 'Add Staff'}
                    </div>
                    <div
                        className="closeBtn "
                        onClick={handleClose}
                    >
                        <i class="fa-solid fa-xmark"></i>
                    </div>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <form autoComplete="off">
                    <input type="text" name="fake-username" autoComplete="username" style={{ display: 'none' }} />
                    <input type="password" name="fake-password" autoComplete="current-password" style={{ display: 'none' }} />

                    <div className="row mt-2">
                        <div className="col-12 mb-1">
                            <Input
                                label="Name"
                                name="name"
                                placeholder="Enter Name"
                                value={formData.name}
                                onChange={handleChange}
                                errorMessage={errors.name}
                                autoComplete="off"
                            />
                        </div>
                        <div className="col-12 mb-1">
                            <Input
                                label="Email"
                                name="email"
                                placeholder="Enter Email"
                                value={formData.email}
                                onChange={handleChange}
                                errorMessage={errors.email}
                                autoComplete="new-email"
                            />
                        </div>
                        {!dialogueData && (<div className="col-12 mb-1">
                            <Input
                                label="Password"
                                name="password"
                                type="password"
                                placeholder="Enter Password"
                                value={formData.password}
                                onChange={handleChange}
                                errorMessage={errors.password || (dialogueData ? "Leave blank if you don't want to change" : "")}
                                autoComplete="new-password"
                            />
                        </div>)}
                        <div className="col-12 mb-1">
                            <label className='form-label mb-2' style={{ fontWeight: 500 }}>Role</label>
                            <FormControl fullWidth size="small" error={!!errors.roleId}>
                                <Select
                                    name="roleId"
                                    value={formData.roleId}
                                    onChange={handleChange}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return <span style={{ color: '#aaa' }}>Select Role</span>;
                                        }
                                        return assignableRoles?.find(r => r._id === selected)?.name || selected;
                                    }}
                                    sx={{
                                        borderRadius: '8px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#ddd',
                                        }
                                    }}
                                >
                                    <MenuItem disabled value="">
                                        <em>Select Role</em>
                                    </MenuItem>
                                    {assignableRoles?.map((role) => (
                                        <MenuItem key={role._id} value={role._id}>
                                            {role.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.roleId && <p className="errorMessage">{errors.roleId}</p>}
                            </FormControl>
                        </div>
                    </div>
                </form>
            </DialogContent>
            <div className="d-flex justify-content-end align-items-center p-3">
                <Button
                    btnName={dialogueData ? 'Update' : 'Submit'}
                    onClick={handleSubmit}
                    btnColor={`btnBlackPrime text-light`}
                    style={{ borderRadius: "5px", width: "80px" }}
                    newClass={`me-2`}
                />
                <Button
                    btnName={`Close`}
                    btnColor="myCustomButton"
                    onClick={handleClose}
                    style={{ borderRadius: "5px", width: "80px" }}
                />
            </div>

        </Dialog>
    );
};

export default StaffDialogue;
